import { Zap, Lightbulb, CheckCircle, Phone, Star, Shield, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '@/components/ui-custom/SEO';

const Electrical = () => {
  const services = [
    {
      title: 'Panel Upgrades',
      description: 'Upgrade your electrical panel to handle modern power demands safely and efficiently.',
      icon: Zap,
    },
    {
      title: 'Wiring & Rewiring',
      description: 'Complete wiring services for new construction, renovations, and older homes.',
      icon: Zap,
    },
    {
      title: 'Lighting Installation',
      description: 'Indoor and outdoor lighting solutions including LED upgrades and smart lighting.',
      icon: Lightbulb,
    },
    {
      title: 'Outlets & Switches',
      description: 'Installation and repair of outlets, switches, and GFCI protection.',
      icon: Zap,
    },
    {
      title: 'Safety Inspections',
      description: 'Comprehensive electrical safety inspections for homes and businesses.',
      icon: Shield,
    },
    {
      title: 'Troubleshooting',
      description: 'Expert diagnosis and repair of electrical issues, big or small.',
      icon: Zap,
    },
  ];

  const whyChooseUs = [
    'Licensed Alabama electricians',
    'Up-to-code guarantee on all work',
    'Mon-Fri 8AM-5PM',
    'Upfront, honest pricing',
    '65+ years combined experience',
    'Family-owned and operated',
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'serviceType': 'Electrical',
    'name': 'Electrical Services in Dothan, AL',
    'description': 'Professional electrical services including panel upgrades, wiring, lighting installation, outlet repair, safety inspections, and troubleshooting throughout the Wiregrass area.',
    'provider': { '@type': 'LocalBusiness', 'name': "Ard's Plumbing & Electric", 'telephone': '+1-334-692-5537', 'url': 'https://ardsdothan.com' },
    'areaServed': { '@type': 'AdministrativeArea', 'name': 'Wiregrass Area, Alabama' },
    'url': 'https://ardsdothan.com/services/electrical',
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Electrical Services',
      'itemListElement': [
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Panel Upgrades' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Wiring & Rewiring' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Lighting Installation' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Outlets & Switches' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Safety Inspections' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Troubleshooting' } },
      ],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      { '@type': 'Question', 'name': 'Are your electricians licensed in Alabama?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, all our electricians hold active Alabama electrical licenses and carry full insurance.' } },
      { '@type': 'Question', 'name': 'How do I know if I need an electrical panel upgrade?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Common signs include frequently tripping breakers, flickering lights, a panel older than 25 years, or not enough outlets. Call us for a free professional assessment.' } },
      { '@type': 'Question', 'name': 'Do you handle both residential and commercial electrical work?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, we perform electrical work for homes, businesses, and commercial properties throughout the Wiregrass area.' } },
      { '@type': 'Question', 'name': 'What areas do you serve for electrical services?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'We serve Dothan and the entire Wiregrass area including Enterprise, Ozark, Geneva, Headland, Midland City, Daleville, Newton, and Rehobeth.' } },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://ardsdothan.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': 'Services', 'item': 'https://ardsdothan.com/services' },
      { '@type': 'ListItem', 'position': 3, 'name': 'Electrical', 'item': 'https://ardsdothan.com/services/electrical' },
    ],
  };

  const faqs = [
    { q: 'Are your electricians licensed in Alabama?', a: 'Yes, all our electricians hold active Alabama electrical licenses and carry full insurance.' },
    { q: 'How do I know if I need an electrical panel upgrade?', a: 'Common signs include frequently tripping breakers, flickering lights, a panel older than 25 years, or not enough outlets. Call us for a free professional assessment.' },
    { q: 'Do you handle both residential and commercial electrical work?', a: 'Yes, we perform electrical work for homes, businesses, and commercial properties throughout the Wiregrass area.' },
    { q: 'What areas do you serve for electrical services?', a: 'We serve Dothan and the entire Wiregrass area including Enterprise, Ozark, Geneva, Headland, Midland City, Daleville, Newton, and Rehobeth.' },
  ];

  return (
    <>
      <SEO
        title="Electrical Services Dothan AL | Ard's Plumbing & Electric"
        description="Professional electrical services in Dothan, AL. Panel upgrades, wiring, lighting, safety inspections & more. Licensed electricians. Call (334) 692-5537."
        keywords="electrician Dothan AL, electrical repair, panel upgrade, wiring services, lighting installation, emergency electrician Wiregrass"
        canonical="/services/electrical"
        additionalSchema={[serviceSchema, faqSchema, breadcrumbSchema]}
      />

      {/* Hero Section */}
      <section className="bg-brand-blue pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 text-white text-sm px-4 py-2 rounded-full mb-6">
                <Zap className="w-4 h-4" />
                Professional Electrical Services
              </div>
              <h1 className="font-heading font-black text-4xl lg:text-6xl text-white leading-tight mb-6">
                ELECTRICAL SERVICES<br />
                <span className="text-brand-yellow">DONE RIGHT</span>
              </h1>
              <p className="text-lg text-white/90 mb-8 max-w-lg">
                Safe, reliable electrical work for your home or business. 
                Our licensed electricians serve Dothan and the entire Wiregrass area.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  className="bg-brand-coral hover:bg-brand-coral/90 text-white rounded-full px-8 py-6 text-lg font-semibold"
                >
                  <Link to="/contact-us">Schedule Service</Link>
                </Button>
                <a
                  href="tel:3346925537"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-brand-blue transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  (334) 692-5537
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/electrical-service.jpg"
                alt="Professional electrical service in Dothan AL"
                className="rounded-[28px] shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-brand-blue rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-white fill-white" />
                  </div>
                  <div>
                    <p className="font-bold text-brand-black">4+ Rating</p>
                    <p className="text-sm text-gray-500">100+ Reviews</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-black text-3xl lg:text-5xl text-brand-black mb-4">
              OUR ELECTRICAL SERVICES
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Complete electrical solutions for homes and businesses throughout the Wiregrass area.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-6 hover:shadow-xl transition-shadow"
              >
                <div className="w-14 h-14 bg-brand-blue/10 rounded-xl flex items-center justify-center mb-4">
                  <service.icon className="w-7 h-7 text-brand-blue" />
                </div>
                <h3 className="font-heading font-bold text-xl text-brand-black mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-brand-navy">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-black text-3xl lg:text-5xl text-white mb-6">
                WHY CHOOSE ARD'S<br />
                <span className="text-brand-yellow">FOR ELECTRICAL?</span>
              </h2>
              <p className="text-lg text-white/80 mb-8">
                Electrical work requires expertise and precision. Our licensed electricians 
                deliver safe, code-compliant solutions for every project.
              </p>
              <ul className="space-y-4">
                {whyChooseUs.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-white">
                    <CheckCircle className="w-5 h-5 text-brand-yellow flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Shield className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">Licensed</p>
                <p className="text-white/70 text-sm">Electricians</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Clock className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">Same-Day</p>
                <p className="text-white/70 text-sm">Service</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Star className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">5-Star</p>
                <p className="text-white/70 text-sm">Rated</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Zap className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">Code</p>
                <p className="text-white/70 text-sm">Compliant</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-black text-3xl lg:text-5xl text-brand-black mb-4">
              FREQUENTLY ASKED QUESTIONS
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-heading font-bold text-lg text-brand-black mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-brand-coral">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-black text-3xl lg:text-5xl text-white mb-6">
            NEED AN ELECTRICIAN NOW?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Don't take chances with electrical work. Trust the licensed professionals at 
            Ard's Plumbing & Electric for safe, reliable service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-white text-brand-coral hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold"
            >
              <Link to="/contact-us">Book Online</Link>
            </Button>
            <a
              href="tel:3346925537"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-brand-coral transition-colors"
            >
              <Phone className="w-5 h-5" />
              Call (334) 692-5537
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Electrical;
